import { memo, useState } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { Avatar } from '@nextui-org/avatar'
import { Button } from '@nextui-org/button'
import { useRouter } from 'next/navigation'
import { useLogout } from '@privy-io/react-auth'

import ICONS from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { cn } from '@/utils/cn'
import { authActions } from '@/redux/reducers/auth/auth.reducer'
import { settingsActions } from '@/redux/reducers/settings/setting.reducer'

// type AccountProps = {}

const Account = memo(() => {
  const router = useRouter()
  const [isShowDropDown, setIsShowDropDown] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.root.auth.user)
  const spaceActive = useAppSelector((state) => state.root.settings.spaceActive)

  const { logout: handleLogout } = useLogout({
    onSuccess: () => {
      dispatch(authActions.logout())
      dispatch(settingsActions.setActiveSpace(''))
      router.push('/sign-in')
    },
  })
  return (
    <div className="mb-2">
      <div
        className={cn('flex justify-between items-center w-full bg-ss-grayscale-900 p-2.5 rounded-lg', {
          'rounded-bl-none rounded-br-none': isShowDropDown,
        })}
      >
        <div className="flex items-center gap-x-1">
          <Link href={`/profile`} className="flex items-center gap-x-1">
            <Avatar
              showFallback
              src={spaceActive ? user?.spaceAvatarUrl : user?.avatarImage}
              alt="avatar"
              className="rounded-full w-6 h-6"
            />
            <p className="text-sm max-w-[120px] truncate"> {spaceActive ? user?.spaceNickname : user?.nickname}</p>
          </Link>
          {spaceActive ? (
            <Image src={ICONS.icVerifySpace} width={14} height={14} alt="Verify" />
          ) : user?.role?.value === 'kol' ? (
            <Image src={ICONS.verifyBadge} width={14} height={14} alt="Verify" />
          ) : null}
        </div>

        <div className="flex items-center gap-x-1 rounded-full">
          {spaceActive && (
            <Link href={`/my-space/${spaceActive}`}>
              <Avatar showFallback src={user?.avatarOrigin || ''} alt="avatar" className="rounded-full w-6 h-6" />
            </Link>
          )}
          <button onClick={() => setIsShowDropDown(!isShowDropDown)}>
            <Image
              className={cn('transition-all duration-300', {
                'rotate-180': isShowDropDown,
              })}
              src={ICONS.arrowDown}
              width={24}
              height={24}
              alt=""
            />
          </button>
        </div>
      </div>
      <div
        className={cn('w-full h-0 bg-ss-grayscale-900 px-2.5 rounded-bl-lg rounded-br-lg transition-all duration-300', {
          'h-auto pb-2.5 delay-100': isShowDropDown,
          'pointer-events-none': !isShowDropDown,
        })}
      >
        <div
          className={cn('opacity-0 space-y-4 transition-all duration-300 max-h-[80svh] overflow-y-auto', {
            'opacity-100 border-t border-white/10 pt-2.5 delay-75': isShowDropDown,
          })}
        >
          {user?.spaces?.map((s) => {
            if (spaceActive === s.id) return

            return (
              <div key={s.id} className="flex items-center gap-x-1 cursor-pointer">
                <div
                  onClick={() => {
                    dispatch(settingsActions.setActiveSpace(s.id))
                    if (!spaceActive) {
                      dispatch(
                        authActions.setUser({
                          ...user,
                          avatarOrigin: user.avatarImage,
                          nicknameOrigin: user.nickname,
                        }),
                      )
                    }
                    dispatch(settingsActions.setMenu(false))
                    router.push(`/my-space/${s.id}?tab=campaigns`)
                  }}
                  className="flex items-center gap-x-1"
                >
                  <Avatar showFallback src={s.profileImageUrl || ''} alt="avatar" className="rounded-full w-6 h-6" />
                  <p className="text-sm truncate">{s.name}</p>
                </div>
                <div>
                  <Image src={ICONS.icVerifySpace} width={14} height={14} alt="Verify" />
                </div>
              </div>
            )
          })}
          {spaceActive && (
            <div className="flex items-center gap-x-1 cursor-pointer">
              <div
                onClick={() => {
                  dispatch(settingsActions.setActiveSpace(''))
                  dispatch(settingsActions.setMenu(false))
                  setIsShowDropDown(false)
                  router.push('/profile')
                }}
                className="flex items-center gap-x-1"
              >
                <Avatar alt={user?.nicknameOrigin} className="flex-shrink-0" size="sm" src={user?.avatarOrigin} />
                <span className="text-small text-white flex-1 line-clamp-1">{user?.nicknameOrigin}</span>
                {user?.role?.value === 'kol' && <Image src={ICONS.verifyBadge} width={14} height={14} alt="Verify" />}
              </div>
            </div>
          )}
          <div>
            <Link
              onClick={() => {
                dispatch(settingsActions.setMenu(false))
              }}
              href={`/spaces/create`}
            >
              <div className="flex items-center gap-x-1">
                <Image src={ICONS.plusCircleWhite} width={14} height={14} alt="Verify" />

                <p className="text-sm">Create new Space</p>
              </div>
            </Link>
          </div>

          {user?.spaces?.length ? (
            <div>
              <Button className="w-full bg-ss-grayscale-800">
                <Link href={'/spaces'}>See all spaces</Link>
              </Button>
            </div>
          ) : null}
          <div className="border-t border-white/10 pt-2.5">
            <Button onClick={handleLogout} className="bg-transparent p-0">
              <Image src={ICONS.signOut} width={14} height={14} alt="Verify" />
              <p className="text-sm">Log out</p>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})

export default Account
